import { useDispatch, useSelector } from 'react-redux';
import { closeModal } from '../../../../Features/inventoryManagementSlice';
import { useEffect, useRef, useState } from 'react';
import SectionTitle from './SectionTitle';
import SizeValueRow from './SizeValueRow';
import showhide from "../../../../Assets/ShowHide.svg";
import { useOutsideClickHandler } from '../../../../Hooks/useOutsideClick';

function SizeChartDetails() {
    const dispatch = useDispatch();
    const wrapperRef = useRef(null);
    useOutsideClickHandler(wrapperRef, () => {
        dispatch(closeModal());
    });

    const [unit, setUnit] = useState('in');
    const [sizeList, setSizeList] = useState([]);
    const [categoryKeys, setCategoryKeys] = useState([]);

    const selectedSizeChart = useSelector((state) => state.inventoryManagement.selectedSizeChart);

    function toggleUnit() {
        setUnit((prev) => (prev === 'in' ? 'cm' : 'in'));
    }

    useEffect(() => {
        if (Object.keys(selectedSizeChart).length > 0) {
            if (selectedSizeChart.sizes.length > 0) {
                let transformedSizes = selectedSizeChart.sizes;
                if (selectedSizeChart.chartType === "Clothing Set") {
                    transformedSizes = selectedSizeChart.sizes.map((row) => {
                        if (row.outSeam) {
                            const { outSeam, ...rest } = row;
                            return { ...rest, outseam: outSeam };
                        }
                        return row;
                    });
                }
                setSizeList(transformedSizes);
            }
        }
    }, [selectedSizeChart]);

    useEffect(() => {
        if (sizeList.length > 0) {
            const first = sizeList[0];
            const keys = Object.keys(first).filter((k) => k !== 'id');
            const ordered = ['sizeName', ...keys.filter((k) => k !== 'sizeName')];
            setCategoryKeys(ordered);
        }
    }, [sizeList]);

    const formatHeaderTitle = (text) => {
        if (!text) return "";
        return text
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (s) => s.toUpperCase())
            .trim();
    };

    return (
        <div
            ref={wrapperRef}
            className="w-fit max-w-[60%] p-4 flex flex-col gap-4 rounded-xl bg-[#FFFFFF] shadow-[0px_0px_12px_0px_#0000003D]"
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <h1 className="font-semibold text-2xl text-[#121212]">
                        {selectedSizeChart.chartName} &#40;
                        {selectedSizeChart.chartType === 'Trousers, Phants, Jeans'
                            ? 'Trousers, Pants, Jeans'
                            : selectedSizeChart.chartType}
                        &#41;
                    </h1>
                </div>

                <div className="flex items-center gap-3 mr-1 text-[14px] leading-5 text-[#666666]">
                    <span>cm</span>
                    <button
                        onClick={toggleUnit}
                        className={`outline-none flex items-center w-11 h-6 p-[2px] rounded-xl ${
                            unit === 'in' ? 'bg-primaryColor' : 'bg-primaryAccent'
                        }`}
                    >
                        <img
                            className={`${
                                unit === 'in' ? 'ml-auto mr-[-2px]' : 'ml-[-2px]'
                            } rounded-full`}
                            src={showhide}
                            alt=""
                        />
                    </button>
                    <span>in</span>
                </div>
            </div>

            <div className="w-full overflow-x-scroll flex flex-col">
                <div className="flex items-center">
                    {categoryKeys.map((key, index) => {
                        const isFirst = index === 0;
                        const isLast = index === categoryKeys.length - 1;

                        if (key === "sizeName") {
                            return (
                                <div
                                    key={key}
                                    className={`w-[180px] flex items-center justify-center shrink-0 h-12 rounded-tl-lg border-t border-l border-b border-[#EAEAEA]`}
                                >
                                    <p className="font-normal text-[16px] leading-[22px] text-[#666666]">
                                        Size
                                    </p>
                                </div>
                            );
                        }

                        return (
                            <SectionTitle
                                key={key}
                                title={formatHeaderTitle(key)}
                                measure={unit}
                                isLast={isLast}
                                isFirst={isFirst}
                            />
                        );
                    })}
                </div>

                {sizeList.map((row, index) => {
                    const isLastRow = index === sizeList.length - 1;
                    return (
                        <div
                            key={row.id}
                            className={`${!isLastRow && "border-b border-[#EAEAEA]"} w-full flex items-center`}
                        >
                            {categoryKeys.map((key, keyIndex) => {
                                const isFirst = keyIndex === 0;
                                const isLast = keyIndex === categoryKeys.length - 1;
                                const value = row[key];

                                if (key === "sizeName") {
                                    return (
                                        <div
                                            key={`${row.id}-${key}`}
                                            className="w-[180px] h-[47px] flex justify-center items-center shrink-0 font-normal text-[16px] leading-[22px] text-[#666666] rounded-bl-lg border-l border-b border-[#EAEAEA]"
                                        >
                                            <p className="text-center">{row.sizeName}</p>
                                        </div>
                                    );
                                }

                                if (value && typeof value === "object" && "cm" in value && "in" in value) {
                                    return (
                                        <SizeValueRow
                                            key={`${row.id}-${key}`}
                                            value={value[unit]}
                                            isLast={isLast}
                                            isFirst={isFirst}
                                        />
                                    );
                                }

                                return (
                                    <SizeValueRow
                                        key={`${row.id}-${key}`}
                                        value={value || ""}
                                        isLast={isLast}
                                        isFirst={isFirst}
                                    />
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default SizeChartDetails;
